import React, { useImperativeHandle, forwardRef, useEffect, useState } from 'react';
import { useErrorInputs } from "../../hooks/useErrorInputs";
import { useNotification } from '../../hooks/useNotification';
import { useDataAuth } from "../../hooks/useDataAuth";
import { useTranslation } from "react-i18next";
import TableContainer from '@mui/material/TableContainer';
import FormControl from '@mui/material/FormControl';
import IconButton from '@mui/material/IconButton';
import CheckIcon from '@mui/icons-material/Check';
import Typography from '@mui/material/Typography';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import EditIcon from '@mui/icons-material/Edit';
import TextField from '@mui/material/TextField';
import TableRow from '@mui/material/TableRow';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Table from '@mui/material/Table';
import Paper from '@mui/material/Paper';
import Grid from '@mui/material/Grid';

const required = (
  <Typography variant="subtitle2" component="span" color="error" sx={{ mr: 0.5 }}>
    *
  </Typography>
);
const productStates = [
    { value: '1', label: 'Bueno' },
    { value: '2', label: 'Faltante' },
    { value: '3', label: 'Dañado' },
    { value: '4', label: 'Vencido' },
    { value: '5', label: 'Sobrante' },
    { value: '6', label: 'Rechazado' },
    { value: '7', label: 'Pendiente' }
];

export const Products = forwardRef(({ dto, handleDirectInputChange, isView }, ref) => {
    const requiredField = ['quantity', 'state'];
    let creating = dto.products.some(product => product.isCreated)
    const [errorInputs, handleErrorInput, resetErrors] = useErrorInputs({});
    const [sendNotification] = useNotification();
    const { t } = useTranslation();
    console.log(isView)
    useImperativeHandle(ref, () => ({
        getData: () => dto, 
        errorInputs,
        validForm: () => validForm()
    }));
    
    
    const handleSaveProduct = (index) => {
        let product = dto.products[index];
        const isValid = requiredField.every((field) => validateField(index, field, product[field]));
        if (isValid) {
            const updatedProductos = dto.products.map((prod, i) => {
                if (i === index) {
                    return { ...prod, isCreated: false, isDisabled: false }; 
                }
                return prod;
            });
            handleDirectInputChange('products', updatedProductos);
            sendNotification({ msg: t('messages.saveSuccess'), variant: 'success' });
            resetErrors();
        } else {
            sendNotification({ msg: t('validation.requiredFieldsMissing'), variant: 'error' });
        }
    };

    const handleEditProduct = (index) => {
        if (creating) return 
        const updatedProductos = dto.products.map((prod, i) =>
            i === index ? { ...prod, isCreated: true, isDisabled:true } : prod
        );
        handleDirectInputChange('products', updatedProductos);
    };

    const validForm = () => {
        if (dto.products.length > 0 && dto.products[0].name && dto.products[0].code && !creating) {
            return true;
        }
        return false;
    };

    const validateField = (index, name, value) => {
        if (['isCreated'].includes(name)) {
            handleErrorInput(`${index}-${name}`, { isError: true, messageError: 'isCreated' });
            return true;
        }

        if (typeof value !== 'string') {
            value = String(value || '');
        }

        if (value.trim() === '') {
            handleErrorInput(`${index}-${name}`, { isError: true, messageError: `${name} es obligatorio.` });
            return false;
        } else {
            handleErrorInput(`${index}-${name}`, { isError: false, messageError: '' });
            return true;
        }
    };

    const handleFieldChange = (index, field, value) => {
            const updatedProductos = dto.products.map((producto, i) => {
                if (i === index) {
                    const updatedProduct = { ...producto, [field]: value };
                    return updatedProduct;
                }
                return producto;
            });
            handleDirectInputChange('products', updatedProductos);
        };
  return (
    <>
        <Grid size={{ xs: 12, sm:12 }}>
            <Typography variant="h6">Confirmación de productos.</Typography>
        </Grid>

        <TableContainer component={Paper} sx={{ mb: 4 }}>
            <Table sx={{ borderCollapse: 'separate' }}>
                <TableHead>
                    <TableRow>
                        <TableCell sx={{ px: 1.5, py: 1 }}>
                            <Typography variant="subtitle2" component="span">{t('crm.products.code')}</Typography>
                        </TableCell>
                        <TableCell sx={{ px: 1.5, py: 1 }}>
                            <Typography variant="subtitle2" component="span">{t('crm.products.name')}</Typography>
                        </TableCell>
                        <TableCell sx={{ px: 1.5, py: 1 }}>
                            <Typography variant="subtitle2" component="span">{required}{t('crm.products.quantity')}</Typography>
                        </TableCell>
                        <TableCell sx={{ px: 1.5, py: 1 }}>
                            <Typography variant="subtitle2" component="span">{t('crm.products.unitOfMeasurement')}</Typography>
                        </TableCell>
                        <TableCell sx={{ px: 1.5, py: 1 }}>
                            <Typography variant="subtitle2" component="span">{required}{`${t('crm.products.state')}`}</Typography>
                        </TableCell>
                        <TableCell sx={{ px: 1.5, py: 1 }}>
                            <Typography variant="subtitle2" component="span">{`${t('crm.products.observation')}`}</Typography>
                        </TableCell>
                        <TableCell sx={{ px: 1.5, py: 1, minWidth: 80 }}>Confirmar</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {dto.products.map((product, index) => (
                    <TableRow key={index} sx={{ backgroundColor: product.isCreated && 'white' }}>
                        <TableCell sx={{ px: 1.5, py: 1 }}>
                            <TextField fullWidth required disabled size="small" value={product.code}/>
                        </TableCell>
                        <TableCell sx={{ px: 1.5, py: 1 }}>
                            <TextField fullWidth required disabled size="small" value={product.name}/>
                        </TableCell>
                        <TableCell sx={{ px: 1.5, py: 1 }}>
                            <TextField fullWidth required disabled={!product.isCreated} size="small" type="number"className="input-no-spinner"
                                value={product.quantity}
                                onChange={(e) => handleFieldChange(index, 'quantity', e.target.value)}
                                error={!!errorInputs[`${index}-quantity`]?.isError}
                            />
                        </TableCell>
                        <TableCell sx={{ px: 1.5, py: 1 }}>
                            <TextField fullWidth required disabled size="small"value={product.unit_description} />
                        </TableCell>
                        <TableCell sx={{ px: 1.5, py: 1 }}>
                            <FormControl fullWidth disabled={!product.isCreated} size="small"
                                error={!!errorInputs.state?.isError}
                            >
                                <Select size="small" value={product.state}
                                    onChange={(e) => handleFieldChange(index, 'state', e.target.value)}
                                    MenuProps={{ PaperProps: { style: { maxHeight: 200, overflowY: 'auto' } }}}
                                >
                                    {productStates.map((p, i) => <MenuItem key={i} value={p.value}>{p.label}</MenuItem>)}
                                </Select>
                            </FormControl>
                        </TableCell>
                                                    {/* <TableCell key={'locationWarehouse'}>
                                <FormControl fullWidth disabled={!product.isCreated} size="small"
                                    error={!!errorInputs.locationWarehouse?.isError}
                                >
                                    <Select size="small" sx={{ width: 120 }}
                                        value={product.locationWarehouse}
                                        onChange={(e) => handleFieldChange(index, 'locationWarehouse', e.target.value)}
                                        MenuProps={{ PaperProps: { style: { maxHeight: 200, overflowY: "auto" }}}}
                                    >
                                        {locationWarehouses.map((p, i) => <MenuItem key={i} value={p.value}> {p.label}</MenuItem>)}
                                    </Select>
                                </FormControl>
                            </TableCell> */}
                        <TableCell sx={{ px: 1.5, py: 1 }}>
                            <TextField fullWidth required disabled={!product.isCreated} size="small" value={product.observation}
                                onChange={(e) => handleFieldChange(index, 'observation', e.target.value)}
                                error={!!errorInputs[`${index}-observation`]?.isError}
                            />
                        </TableCell>
                        <TableCell sx={{ px: 1.5, py: 1 }}>
                            <IconButton
                                color={product.isCreated ? 'success' : 'primary'}
                                disabled={isView}
                                onClick={() => product.isCreated
                                    ? handleSaveProduct(index)
                                    : handleEditProduct(index)
                                }
                                title={product.isCreated ? 'Guardar' : 'Editar'}
                            >
                                {product.isCreated ? <CheckIcon /> : <EditIcon />}
                            </IconButton>
                        </TableCell>
                    </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    </>

  )
});
